import React, { useState, useEffect } from 'react';
import { Plus, Key, Trash2, X, Pencil } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../utils/toast';
import { criarAcessoNoServidor, avisarUsuariosAlterados } from '../utils/criarAcesso';

export function Usuarios({ perfisAcesso = [], isAdmin = false }) {
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null); // id do usuário sendo editado
  const [form, setForm] = useState({ nome: '', email: '', senha: '', papel: 'admin', profissional: '', perfilAcessoId: '', empresa: '' });
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setCarregando(true);
    const { data } = await supabase.from('perfis').select('*').order('criado_em');
    setUsuarios(data || []);
    setCarregando(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => {
    setEditando(null);
    setForm({ nome: '', email: '', senha: '', papel: 'consultor', profissional: '', perfilAcessoId: '' });
    setErro('');
    setModalAberto(true);
  };

  const abrirEdicao = (u) => {
    setEditando(u.id);
    setForm({ nome: u.nome || '', email: u.email || '', senha: '', papel: u.papel || 'consultor', profissional: u.profissional || '', perfilAcessoId: u.perfil_acesso_id ? String(u.perfil_acesso_id) : '', empresa: u.empresa || '' });
    setErro('');
    setModalAberto(true);
  };

  const salvar = async () => {
    if (editando) {
      // Edição: apenas atualiza o perfil na tabela
      if (!form.nome) { setErro('Nome é obrigatório.'); return; }
      setErro('');
      const { error } = await supabase.from('perfis').update({
        nome: form.nome,
        papel: form.papel,
        profissional: form.profissional,
        perfil_acesso_id: form.perfilAcessoId || null,
        empresa: form.empresa || null,
      }).eq('id', editando);
      if (error) { setErro('Erro ao salvar: ' + error.message); return; }
      toast.sucesso(`Perfil de "${form.nome}" atualizado.`);
      avisarUsuariosAlterados();
    } else {
      // Criação — blindada contra "already registered" após exclusão
      if (!form.nome || !form.email || !form.senha) { setErro('Nome, e-mail e senha são obrigatórios.'); return; }
      setErro('');

      // Cadastro pelo servidor (api/criar-acesso): conta já confirmada, perfil
      // gravado e boas-vindas da Kzero, sem o e-mail do Supabase (16/10/2026).
      const r = await criarAcessoNoServidor({
        nome: form.nome,
        email: form.email,
        senha: form.senha,
        papel: form.papel,
        profissional: form.profissional || null,
        empresa: form.empresa || null,
        perfil_acesso_id: form.perfilAcessoId || null,
      });
      if (!r.ok) {
        setErro(r.erro);
        return;
      }

      toast.sucesso(
        (r.ja_existia
          ? `Perfil de "${form.nome}" vinculado ao usuário existente.`
          : `Usuário "${form.nome}" criado com sucesso!`)
        + (r.email_enviado ? ' E-mail de boas-vindas enviado.' : ' Não foi possível enviar o e-mail de boas-vindas.')
      );
    }

    setModalAberto(false);
    setEditando(null);
    setForm({ nome: '', email: '', senha: '', papel: 'consultor', profissional: '', perfilAcessoId: '', empresa: '' });
    carregar();
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este usuário? (será removido também do auth)')) return;
    const { data, error } = await supabase.rpc('excluir_perfil_completo', { p_user_id: id });
    if (error || !data?.ok) {
      toast.erro('Erro ao excluir: ' + (error?.message || 'falha desconhecida'));
      return;
    }
    toast.sucesso('Usuário excluído por completo.');
    carregar();
  };

  const enviarRedefinicao = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) {
      toast.erro('Erro ao enviar e-mail: ' + error.message);
    } else {
      toast.sucesso(`E-mail de redefinição enviado para ${email}.`);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.titulo}>Gerenciar Usuários</h2>
        <button style={s.btnNovo} onClick={abrirNovo}><Plus size={16}/> Novo usuário</button>
      </div>

      <div style={s.tabelaCard}>
        <table style={s.tabela}>
          <thead>
            <tr>
              {['Nome','E-mail de Login','Perfil de Acesso','Módulos','Criado em','Ações'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {carregando
              ? <tr><td colSpan={6} style={s.vazio}>Carregando...</td></tr>
              : usuarios.length === 0
                ? <tr><td colSpan={6} style={s.vazio}>Nenhum usuário.</td></tr>
                : usuarios.map(u => {
                    const perfilAcesso = perfisAcesso.find(p => String(p.id) === String(u.perfil_acesso_id));
                    const isAdminUser = u.papel === 'admin';
                    const modulosVisiveis = perfilAcesso?.permissoes
                      ? Object.entries(perfilAcesso.permissoes).filter(([, v]) => v?.ver === true && !String('').includes('.')).map(([k]) => k).slice(0, 4)
                      : [];
                    return (
                      <tr key={u.id}>
                        <td style={s.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <strong>{u.nome}</strong>
                            {isAdminUser && <span style={{ ...s.badge, ...s.badgeAdmin }}>Admin</span>}
                          </div>
                        </td>
                        <td style={s.td}>{u.email}</td>
                        <td style={s.td}>
                          {isAdminUser
                            ? <span style={{ color:'#5f5874', fontSize:12 }}>Acesso total</span>
                            : perfilAcesso
                              ? <span style={s.badgeAcesso}>🛡 {perfilAcesso.nome}</span>
                              : <span style={{ color:'#ef4444', fontSize:12 }}>Sem perfil</span>
                          }
                        </td>
                        <td style={s.td}>
                          {isAdminUser
                            ? <span style={{ color:'#5f5874', fontSize:11 }}>Todos</span>
                            : perfilAcesso?.permissoes
                              ? <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                  {Object.entries(perfilAcesso.permissoes)
                                    .filter(([k, v]) => v?.ver === true && !k.includes('.'))
                                    .map(([k]) => <span key={k} style={s.badgeMod}>{k}</span>)}
                                  {!Object.entries(perfilAcesso.permissoes).some(([k, v]) => v?.ver === true && !k.includes('.')) && (
                                    <span style={{ color:'#5f5874', fontSize:11 }}>Nenhum</span>
                                  )}
                                </div>
                              : <span style={{ color:'#5f5874', fontSize:11 }}>—</span>
                          }
                        </td>
                        <td style={s.td}>{u.criado_em ? new Date(u.criado_em).toLocaleDateString('pt-BR') : '—'}</td>
                        <td style={s.td}>
                          <div style={s.acoes}>
                            <button style={s.btnEditPencil} title="Editar usuário" onClick={() => abrirEdicao(u)}><Pencil size={14}/></button>
                            <button style={s.btnEdit} title="Enviar e-mail de redefinição de senha" onClick={() => enviarRedefinicao(u.email)}><Key size={14}/></button>
                            <button style={s.btnDel} onClick={() => excluir(u.id)}><Trash2 size={14}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
            }
          </tbody>
        </table>
      </div>

      {modalAberto && (
        <div style={s.overlay} onClick={() => setModalAberto(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitulo}>{editando ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <button style={s.modalFechar} onClick={() => setModalAberto(false)}><X size={18}/></button>
            </div>
            {erro && <p style={{ color: '#ef4444', fontSize: 13 }}>{erro}</p>}
            {[
              { label: 'Nome *', key: 'nome', type: 'text', show: true },
              { label: 'E-mail *', key: 'email', type: 'email', show: !editando },
              { label: 'Senha *', key: 'senha', type: 'password', show: !editando },
              { label: 'Profissional (ex: Bianca)', key: 'profissional', type: 'text', show: true },
              { label: 'Empresa', key: 'empresa', type: 'text', show: true },
            ].filter(f => f.show).map(f => (
              <div key={f.key} style={s.campo}>
                <label style={s.label}>{f.label}</label>
                <input type="text" autoComplete="off" data-lpignore="true" style={f.type === 'password' ? { ...s.input, WebkitTextSecurity: 'disc' } : s.input} value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} />
              </div>
            ))}
            <div style={s.campo}>
              <label style={s.label}>Perfil de Acesso</label>
              <select style={s.input} value={form.perfilAcessoId} onChange={e => setForm({...form, perfilAcessoId: e.target.value})}>
                <option value="">Sem perfil definido</option>
                {perfisAcesso.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
              <p style={{ fontSize:11, color:'#5f5874', margin:0 }}>Define quais módulos e ações este usuário pode acessar.</p>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="checkbox" id="chk-admin" checked={form.papel === 'admin'} onChange={e => setForm({...form, papel: e.target.checked ? 'admin' : 'consultor'})} style={{ width:16, height:16, cursor:'pointer' }} />
              <label htmlFor="chk-admin" style={{ ...s.label, textTransform:'none', fontSize:13, cursor:'pointer', margin:0 }}>Administrador — acesso total ao sistema (ignora perfil de acesso)</label>
            </div>
            <div className="mobile-modal-footer" style={s.modalFooter}>
              <button style={s.btnCancelar} onClick={() => setModalAberto(false)}>Cancelar</button>
              <button style={s.btnSalvar} onClick={salvar}>{editando ? 'Salvar alterações' : 'Criar usuário'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  container:{display:'flex',flexDirection:'column',gap:'1.5rem'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'center'},
  titulo:{fontSize:'1.8rem',fontWeight:700,color:'#1c1530'},
  btnNovo:{display:'flex',alignItems:'center',gap:6,background:'#7c3aed',color:'white',border:'none',borderRadius:20,padding:'9px 20px',fontSize:13,fontWeight:600,cursor:'pointer'},
  tabelaCard:{background:'#ffffff',border:'1px solid #e5e0ef',borderRadius:12,overflow:'hidden'},
  tabela:{width:'100%',borderCollapse:'collapse'},
  th:{padding:'0.875rem 1rem',textAlign:'left',fontSize:12,fontWeight:600,color:'#5f5874',textTransform:'uppercase',background:'#f7f5fb',borderBottom:'1px solid #e5e0ef'},
  td:{padding:'0.875rem 1rem',fontSize:13,color:'#1c1530',borderBottom:'1px solid #eeeaf5'},
  vazio:{padding:'2rem',textAlign:'center',color:'#5f5874',fontSize:13},
  badge:{fontSize:12,fontWeight:600,borderRadius:20,padding:'3px 12px',border:'1.5px solid'},
  badgeAdmin:{color:'#8b5cf6',borderColor:'#8b5cf6',background:'rgba(139,92,246,0.1)'},
  badgeUser:{color:'#5f5874',borderColor:'#e5e0ef',background:'transparent'},
  badgeInstrutor:{color:'#f59e0b',borderColor:'#f59e0b',background:'rgba(245,158,11,0.1)'},
  badgeAluno:{color:'#3b82f6',borderColor:'#3b82f6',background:'rgba(59,130,246,0.1)'},
  badgeAcesso:{fontSize:11,fontWeight:600,borderRadius:20,padding:'2px 10px',background:'rgba(16,185,129,0.1)',color:'#10b981',border:'1px solid rgba(16,185,129,0.3)'},
  badgeMod:{fontSize:10,fontWeight:600,borderRadius:4,padding:'1px 6px',background:'rgba(88,166,255,0.1)',color:'#1d4ed8',border:'1px solid rgba(88,166,255,0.2)'},
  acoes:{display:'flex',gap:4},
  btnEditPencil:{background:'transparent',border:'none',color:'#10b981',cursor:'pointer',padding:4,display:'flex'},
  btnEdit:{background:'transparent',border:'none',color:'#8b5cf6',cursor:'pointer',padding:4,display:'flex'},
  btnDel:{background:'transparent',border:'none',color:'#ef4444',cursor:'pointer',padding:4,display:'flex'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'1rem'},
  modal:{background:'#ffffff',border:'1px solid #e5e0ef',borderRadius:14,padding:'1.5rem',width:'100%',maxWidth:440,display:'flex',flexDirection:'column',gap:'1rem'},
  modalHeader:{display:'flex',justifyContent:'space-between',alignItems:'center'},
  modalTitulo:{color:'#1c1530',fontSize:18,fontWeight:700},
  modalFechar:{background:'transparent',border:'none',color:'#5f5874',cursor:'pointer',display:'flex'},
  modalFooter:{display:'flex',justifyContent:'flex-end',gap:'0.75rem',marginTop:'0.5rem'},
  campo:{display:'flex',flexDirection:'column',gap:6},
  label:{fontSize:12,fontWeight:600,color:'#5f5874',textTransform:'uppercase',letterSpacing:'0.05em'},
  input:{background:'#f7f5fb',border:'1px solid #e5e0ef',borderRadius:8,color:'#1c1530',padding:'9px 12px',fontSize:14,outline:'none',width:'100%'},
  btnCancelar:{background:'transparent',border:'1px solid #e5e0ef',borderRadius:8,color:'#5f5874',cursor:'pointer',padding:'8px 16px',fontSize:13},
  btnSalvar:{background:'#7c3aed',border:'none',borderRadius:8,color:'white',cursor:'pointer',padding:'8px 18px',fontSize:13,fontWeight:600},
};
